import { useEffect, useMemo } from "react";

import { createPortal } from "react-dom";

import { useUser } from "@components/user-context";
import {
  Flyout,
  SalesTable,
  SearchForm,
  usePageConfiguration,
} from "@features/sales-v3";
import { useTableState } from "@features/sales-v3/hooks/use-table-state";
import { DateFilters } from "@features/sales-v3/top-level-filters/components/date-filters";
import { GET_OVERVIEW_METRICS_REPORT } from "@gql/overview-v2";
import { useNorthbeamQuery } from "@hooks/http";
import {
  makeOverviewRows,
  type GetOverviewMetricsReport,
  type GetOverviewMetricsReportVariables,
} from "@north-beam/nb-client-side";

import { ConfirmCheckbox } from "../components/confirm-checkbox";
import { type OrdersApiStepMetadata } from "../orders-api/step-metadata";

import { MetricCard } from "./metric-card";

interface Props {
  stepMetadata: OrdersApiStepMetadata;
  updateStepMetadata: (data: OrdersApiStepMetadata) => void;
}

export const AttributionTable = ({
  stepMetadata,
  updateStepMetadata,
}: Props) => {
  const { user } = useUser();
  const { dateRangeInterval } = usePageConfiguration();
  const { setTableState } = useTableState();

  useEffect(() => {
    setTableState((prev) => ({
      ...prev,
      columnConfigs: [
        { id: "spend", width: 140, showMiniGraph: false },
        { id: "visits", width: 140, showMiniGraph: false },
      ],
    }));
  }, []);

  const { revMetric, txnsMetric } = useMemo(() => {
    const metrics = makeOverviewRows(
      "northbeam_custom",
      "infinity",
      false,
      "cash",
      [],
      "daily",
      user as any,
    );

    const rev = metrics.find((e) => e.metadata?.overviewPageMetricId === "rev");
    const txns = metrics.find(
      (e) => e.metadata?.overviewPageMetricId === "txns",
    );

    return { revMetric: rev, txnsMetric: txns } as const;
  }, [user]);

  const { data, loading } = useNorthbeamQuery<
    GetOverviewMetricsReport,
    GetOverviewMetricsReportVariables
  >(GET_OVERVIEW_METRICS_REPORT, {
    variables: {
      attributionMethod: "northbeam_custom",
      accountingMode: "cash",
      timeGranularity: "daily",
      aggregateAll: true,
      disableDateCompression: true,
      attributionWindow: "infinity",
      dateRange: dateRangeInterval,
      metrics: ["rev", "txns"],
    },
  });

  const { revValue, txnsValue } = useMemo(() => {
    const conversion = data?.me.overviewMetricsReport.rows?.[0].metrics?.[0];

    if (conversion && revMetric && txnsMetric) {
      return {
        revValue: revMetric.calculate(conversion),
        txnsValue: txnsMetric.calculate(conversion),
      };
    } else {
      return { revValue: null, txnsValue: null };
    }
  }, [data, revMetric, txnsMetric]);

  return (
    <div className="flex items-stretch gap-4 mt-6">
      <div className="shrink-0 w-[320px] p-4 bg-cosmic-5 rounded-lg border border-gray-0">
        <div className="basis-0 text-xl font-semibold leading-3 mt-2 mb-6">
          Steps to verify:
        </div>
        <ul className="text-xs space-y-5 list-decimal ml-3 mb-0 font-bold">
          <li>
            Confirm all the platforms connected are showing up in the table
          </li>
          <li>
            Validating Spend on Platforms, Campaigns, Adsets and Ads
            <ul className="space-y-1 list-[lower-alpha] ml-3 mt-2 font-normal">
              <li>
                If Spend = 0 or does not match what you’re seeing in platform:
                <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
                  <li> Ensure the spend platform is correctly connected</li>
                  <li> Ensure the correct ad accounts are connected</li>
                  <li>
                    Check the time zone in the “Business Information” section
                    matches your in platform time zone
                  </li>
                </ul>
              </li>
              <li>
                Verify the spend data in the reporting tool with the original
                platform(s) to confirm alignment.
              </li>
            </ul>
          </li>
          <li>
            Validating Visits on Platforms, Campaigns, Adsets and Ads
            <ul className="space-y-1 list-[lower-alpha] ml-3 mt-2 font-normal">
              <li>
                If Visits = 0 or does not match:
                <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
                  <li>
                    Ensure that you have setup your pixel in all site pages and
                    pages where paid traffic is directing
                  </li>
                  <li>
                    Check that any third-party landing page redirects with the
                    UTM attached
                  </li>
                  <li>Confirm UTMs are carried through the redirects</li>
                  <li>
                    Validate that UTMs are appended correctly across all ad
                    links
                  </li>
                </ul>
              </li>
            </ul>
          </li>
          <li>
            Validate Revenue
            <ul className="space-y-1 list-[lower-alpha] ml-3 mt-2 font-normal">
              <li>
                Confirm the total Revenue matches internal reporting by
                cross-checking revenue figures with platform reports
              </li>
            </ul>
          </li>
          <li>
            Validate Orders
            <ul className="space-y-1 list-[lower-alpha] ml-3 mt-2 font-normal">
              <li>
                Confirm the total Orders numbers matches internal reporting by
                cross-checking revenue figures with platform reports
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div className="overflow-hidden">
        <div className="mb-4 flex items-stretch gap-4">
          <MetricCard
            title="Revenue"
            value={revValue}
            metric={revMetric}
            loading={loading}
          />
          <MetricCard
            title="Orders"
            value={txnsValue}
            metric={txnsMetric}
            loading={loading}
            precision="approximate"
          />
          <ConfirmCheckbox
            id="verify-setup-confirmation"
            checked={!!stepMetadata.isDataVerified}
            onChange={(checked) => {
              updateStepMetadata({
                ...stepMetadata,
                isDataVerified: checked,
              });
            }}
            showSuccess
          >
            <span className="text-sm">
              I’ve verified Spend, Visit, Revenue and Orders
            </span>
          </ConfirmCheckbox>
        </div>
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex-1 max-w-[380px]">
            <SearchForm />
          </div>
          <DateFilters />
        </div>
        <SalesTable
          isConfigurationDetailsModalOpen={false}
          onCloseConfigurationDetailsModal={() => null}
          hideCustomMetrics={true}
          hideExportTable={true}
          hideCustomizeTable={true}
          hideExtraOptions={true}
          hideSearchForm={true}
          hideStatusColumn={true}
        />
      </div>
      {createPortal(
        <Flyout hideAdObjectPageLink={true} />,
        document.getElementById("flyout-portal")!,
      )}
    </div>
  );
};
